import {bootstrapApplication} from '@angular/platform-browser';
import {provideRouter} from '@angular/router';
import {importProvidersFrom} from '@angular/core';
import {BrowserModule} from '@angular/platform-browser';
import {HttpClientModule, HTTP_INTERCEPTORS} from '@angular/common/http';

import {AppComponent} from './app/app.component';
import {routes} from './app/app.routes';
import {appVersion} from '@/environments/version';
import {AuthInterceptor} from './app/core/auth/auth.interceptor';
import {CancelPaginatedRequestsInterceptor} from './app/core/cancel-paginated-requests.interceptor';
import {ApiErrorInterceptor} from './app/core/api-error.interceptor';

const VERSION_STORAGE_KEY = '__app_version__';
const PRESERVED_LOCAL_STORAGE_KEYS = ['access', 'token', 'expiresAt'];

async function enforceLatestVersion(version: string): Promise<boolean> {
  if (typeof window === 'undefined' || !version) {
    return true;
  }

  try {
    const {localStorage, sessionStorage} = window;

    if (!localStorage) {
      return true;
    }

    const storedVersion = localStorage.getItem(VERSION_STORAGE_KEY);

    if (!storedVersion) {
      localStorage.setItem(VERSION_STORAGE_KEY, version);
      return true;
    }

    if (storedVersion === version) {
      return true;
    }

    const preservedEntries = PRESERVED_LOCAL_STORAGE_KEYS.reduce<Map<string, string>>((acc, key) => {
      const value = localStorage.getItem(key);
      if (value) {
        acc.set(key, value);
      }
      return acc;
    }, new Map());

    localStorage.clear();

    if (typeof sessionStorage !== 'undefined' && sessionStorage) {
      try {
        sessionStorage.clear();
      } catch (clearError) {
        console.warn('[Version Sync] Unable to clear session storage', clearError);
      }
    }

    preservedEntries.forEach((value, key) => {
      localStorage.setItem(key, value);
    });

    localStorage.setItem(VERSION_STORAGE_KEY, version);

    if ('caches' in window && typeof caches?.keys === 'function') {
      try {
        const cacheKeys = await caches.keys();
        await Promise.all(cacheKeys.map(name => caches.delete(name)));
      } catch (cacheError) {
        console.warn('[Version Sync] Unable to clear CacheStorage', cacheError);
      }
    }

    window.location.reload();
    return false;
  } catch (error) {
    console.warn('[Version Sync] Failed to enforce version refresh', error);
    return true;
  }
}

async function main(): Promise<void> {
  const shouldBootstrap = await enforceLatestVersion(appVersion);

  if (!shouldBootstrap) {
    return;
  }

  await bootstrapApplication(AppComponent, {
    providers: [
      importProvidersFrom(BrowserModule, HttpClientModule),
      provideRouter(routes),
      {
        provide: HTTP_INTERCEPTORS,
        useClass: ApiErrorInterceptor,
        multi: true
      },
      {
        provide: HTTP_INTERCEPTORS,
        useClass: AuthInterceptor,
        multi: true
      },
      {
        provide: HTTP_INTERCEPTORS,
        useClass: CancelPaginatedRequestsInterceptor,
        multi: true
      }
    ]
  });

  if (typeof window !== 'undefined') {
    (window as any).__APP_VERSION__ = appVersion;
  }
}

main().catch(err => console.error(err));
